import { useMemo, useState } from "react";
import type { DataSnapshot } from "../lib/types";
import { navigateToSkill } from "../router";
import { useWorkbench } from "../store/workbench";
import { usePipeline } from "../store/pipeline";
import { useScoreAction } from "../hooks/useActions";
import { useToast } from "../store/toast";
import { exportSkillsCsv, exportSkillsJson } from "../lib/export";
import { BrandRail } from "../components/BrandRail";
import { FreshnessBar } from "../components/FreshnessBar";
import { fetchApiHealth } from "../lib/live-api";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "../lib/query-client";

interface Props {
  data: DataSnapshot;
}

export function Workbench({ data }: Props) {
  const { stats, meta, skills } = data;
  const selected = useWorkbench((s) => s.selected);
  const toggle = useWorkbench((s) => s.toggle);
  const selectAll = useWorkbench((s) => s.selectAll);
  const clear = useWorkbench((s) => s.clear);
  const has = useWorkbench((s) => s.has);
  const score = useScoreAction();
  const setRun = usePipeline((s) => s.setRun);
  const toast = useToast((s) => s.push);
  const [filter, setFilter] = useState<"attention" | "all">("attention");

  const { data: health } = useQuery({
    queryKey: queryKeys.health,
    queryFn: fetchApiHealth,
    staleTime: 120_000,
  });

  const pool = useMemo(() => {
    if (filter === "all") return skills;
    return skills.filter(
      (s) => s.combinedPercentage < 85 || s.brokenRefs.length > 0 || s.status !== "healthy",
    );
  }, [skills, filter]);

  const picked = useMemo(
    () => skills.filter((s) => has(s.name)),
    [skills, selected, has]
  );

  const issueCount = (stats.statusCounts?.warning ?? 0) + (stats.statusCounts?.critical ?? 0);
  const attentionCount = skills.filter((s) => s.combinedPercentage < 85).length;

  async function dispatchScore(mode: "missing" | "fallbacks") {
    if (!health?.actionsReady) {
      toast("GITHUB_TOKEN required on Pages", { kind: "error" });
      return;
    }
    try {
      const r = await score.mutateAsync({ mode });
      if (r.run?.id) setRun(r.run.id, `Score ${mode}`);
      toast(`Dispatched ${mode}`, { kind: "info", href: r.run?.html_url });
    } catch (e) {
      toast(e instanceof Error ? e.message : "Failed", { kind: "error" });
    }
  }

  return (
    <div className="grid min-h-screen" style={{ gridTemplateColumns: "220px 1fr" }}>
      <BrandRail counts={{ monitor: stats.total, issues: issueCount }} />
      <main className="min-w-0 pb-16">
        <FreshnessBar meta={meta} />

        <header className="px-6 pt-6 pb-4 border-b border-fg">
          <div className="flex flex-wrap items-baseline justify-between gap-3 mb-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
              // Batch queue · export · workflow dispatch
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
              {skills.length} skills · {attentionCount} need attention
            </span>
          </div>

          <div className="flex flex-wrap items-end gap-x-6 gap-y-3">
            <h1 className="font-display font-extrabold leading-[0.86] tracking-[-0.045em] text-t-72">
              Workbench
            </h1>
            <div className="flex flex-col gap-1 pb-3">
              <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-muted">
                Selection
              </span>
              <span className="font-display font-extrabold text-t-40 tabular-nums leading-[0.95] tracking-[-0.03em]">
                {picked.length}<span className="text-muted text-t-20">/{skills.length}</span>
              </span>
            </div>
          </div>

          <div className="editorial-rule mt-4" />
        </header>

        <section className="px-6 py-4 border-b border-fg flex flex-wrap gap-2 items-center">
          <FilterChip active={filter === "attention"} onClick={() => setFilter("attention")}>
            Needs attention ({attentionCount})
          </FilterChip>
          <FilterChip active={filter === "all"} onClick={() => setFilter("all")}>
            All skills
          </FilterChip>
          <span className="mx-2 h-4 w-px bg-fg" />
          <FilterChip active={false} onClick={() => selectAll(pool.map((s) => s.name))}>
            Select visible ({pool.length})
          </FilterChip>
          <FilterChip active={false} onClick={() => clear()}>
            Clear
          </FilterChip>
        </section>

        <section className="px-6 py-4 border-b border-fg flex flex-wrap gap-2">
          <DispatchButton
            disabled={picked.length === 0}
            onClick={() => exportSkillsCsv(picked)}
            label={`Export CSV${picked.length > 0 ? ` (${picked.length})` : ""}`}
          />
          <DispatchButton
            disabled={picked.length === 0}
            onClick={() => exportSkillsJson(picked)}
            label="Export JSON"
          />
          <span className="mx-2 h-4 w-px bg-fg" />
          <DispatchButton onClick={() => void dispatchScore("missing")} label="Score missing (all)" />
          <DispatchButton onClick={() => void dispatchScore("fallbacks")} label="Rescore fallbacks" />
        </section>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] min-h-[400px]">
          <ul className="border-r border-fg max-h-[70vh] overflow-y-auto min-w-0" role="list" aria-label="Skill pool">
            {pool.length === 0 && (
              <li className="p-8 font-mono text-[11px] text-muted uppercase">// no skills in this filter</li>
            )}
            {pool.map((s) => (
              <li key={s.name} className="border-b border-rule">
                <label className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-[var(--hover)]">
                  <input
                    type="checkbox"
                    checked={has(s.name)}
                    onChange={() => toggle(s.name)}
                    className="accent-fg"
                    aria-label={`Select ${s.name}`}
                  />
                  <span className="font-mono text-[12px] uppercase flex-1 truncate">{s.name}</span>
                  <span
                    className={`font-mono text-[12px] tabular-nums font-bold ${
                      s.combinedPercentage >= 64 ? "text-good" : s.combinedPercentage >= 40 ? "text-warn" : "text-bad"
                    }`}
                  >
                    {Math.round(s.combinedPercentage)}%
                  </span>
                </label>
              </li>
            ))}
          </ul>
          <aside className="p-6 bg-bg">
            <div className="font-mono text-[10px] uppercase tracking-[0.16em] text-muted mb-3 font-medium">
              // Selection ({picked.length})
            </div>
            {picked.length === 0 ? (
              <div className="border border-dashed border-rule p-6 text-center">
                <p className="font-display font-extrabold text-t-26 leading-none tracking-[-0.03em]">
                  Empty.
                </p>
                <p className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted mt-2">
                  Select skills to export or review.
                </p>
              </div>
            ) : (
              <ul className="flex flex-col gap-1.5">
                {picked.map((s) => (
                  <li key={s.name} className="flex justify-between gap-2 border border-fg px-3 py-2 hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-[2px_2px_0_0_var(--fg)] transition-all duration-[80ms]">
                    <button
                      type="button"
                      className="font-mono text-[11px] uppercase hover:underline text-left truncate"
                      onClick={() => navigateToSkill(s.name)}
                    >
                      {s.name}
                    </button>
                    <span className="font-mono text-[10px] text-muted shrink-0 uppercase tracking-[0.1em]">
                      {s.tier}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
}

function FilterChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`font-mono text-[10px] uppercase tracking-[0.12em] px-3 py-1.5 border font-medium ${
        active ? "bg-fg text-bg border-fg" : "border-fg text-muted hover:bg-[var(--hover)] hover:text-fg"
      }`}
    >
      {children}
    </button>
  );
}

function DispatchButton({ onClick, disabled, label }: { onClick: () => void; disabled?: boolean; label: string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="font-mono text-[10px] uppercase tracking-[0.12em] px-3 py-2 border border-fg hover:bg-fg hover:text-bg disabled:opacity-40 disabled:hover:bg-bg disabled:hover:text-fg font-medium"
    >
      {label}
    </button>
  );
}
